import type { FC, RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { getDocumentationUrl } from '@gonfalon/docs/documentationUrl';
import {
  enableObservabilityAiSuggestedPrompts,
  enableVegaAutomatedRollout,
  githubVegaAppUrlSlug,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import { toDiagnosticUsage, toHref, toVegaSettings } from '@gonfalon/navigator/navigators';
import { useGetVegaIntegrationStatusQuery } from '@gonfalon/observability-graphql/hooks';
import { Box } from '@gonfalon/observability-ui/components/Box';
import { Callout } from '@gonfalon/observability-ui/components/Callout';
import { GitHubRepoSelector } from '@gonfalon/observability-ui/components/GitHubRepoSelector';
import { IconSolidGithub } from '@gonfalon/observability-ui/components/icons';
import { Stack } from '@gonfalon/observability-ui/components/Stack';
import { Text } from '@gonfalon/observability-ui/components/Text';
import { AI_BOT_NAME } from '@gonfalon/observability-ui/constants';
import { useProjectId } from '@gonfalon/observability-ui/hooks/useProjectId';
import { useSafeLocalStorage as useLocalStorage } from '@gonfalon/observability-ui/hooks/useSafeLocalStorage';
import { themeVars } from '@gonfalon/observability-ui/theme';
import { getEnvironmentsByProjectQuery } from '@gonfalon/rest-api/queries/environments';
import { getReleasePoliciesQuery } from '@gonfalon/rest-api/queries/releasePolicies';
import { Button, IconButton } from '@launchpad-ui/components';
import type { IconName } from '@launchpad-ui/icons';
import { Icon } from '@launchpad-ui/icons';
import { useQuery } from '@tanstack/react-query';

import { useAgentInsights } from '../../AgentInsightsContext';
import type { ExternalSourceType } from '../../AgentInsightsContext';
import { buildPrompt, mergeAndDedupeResources } from '../../agentInsightsUtils';
import type { VegaMentionsInputHandle } from '../../components/mentions';
import { VegaMentionsInput } from '../../components/mentions';
import { isExternalSourceType } from '../../resources';
import {
  trackVegaAiGithubConnectClicked,
  trackVegaAiModeChanged,
  trackVegaAiPromptSent,
  trackVegaAiSuggestedPromptClicked,
} from '../../vegaAnalytics';

import { getApplicableReleasePolicyForEnvironment } from './getApplicableReleasePolicyForEnvironment';
import { PermissionModePicker } from './PermissionModePicker';
import type { ApplicablePolicyInfo, RolloutReleaseType } from './RolloutEnvironmentSelector';
import { DEFAULT_ROLLOUT_RELEASE_TYPE, RolloutEnvironmentSelector } from './RolloutEnvironmentSelector';
import { SendStopButton } from './SendStopButton';
import { useRotatingPrompts } from './useRotatingPrompts';

import * as styles from './AgentInsights.css';

const DRAFT_PROMPT_STORAGE_KEY = 'vega-draft-prompt';

function saveDraftToStorage(text: string) {
  try {
    if (text) {
      localStorage.setItem(DRAFT_PROMPT_STORAGE_KEY, text);
    } else {
      localStorage.removeItem(DRAFT_PROMPT_STORAGE_KEY);
    }
  } catch {
    // ignore storage errors
  }
}

function loadDraftFromStorage(): string {
  try {
    return localStorage.getItem(DRAFT_PROMPT_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

function clearDraftFromStorage() {
  try {
    localStorage.removeItem(DRAFT_PROMPT_STORAGE_KEY);
  } catch {
    // ignore storage errors
  }
}

/**
 * Converts a plain-text draft (with `\n` line breaks) to Tiptap-compatible
 * HTML so that multi-line content is faithfully restored. Each line becomes a
 * `<p>` element; empty lines use `<br>` to prevent collapsing.
 * Special HTML characters in the text are escaped to avoid parse issues.
 */
function draftToTiptapHtml(text: string): string {
  if (!text) {
    return '';
  }
  return text
    .split('\n')
    .map((line) => {
      const escaped = line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      return `<p>${escaped || '<br>'}</p>`;
    })
    .join('');
}

function externalSourceLabel({ url, sourceType }: { url: string; sourceType: ExternalSourceType }): string {
  if (sourceType === 'github') {
    try {
      const { pathname } = new URL(url);
      if (/^\/[^/]+\/[^/]+\/pull\//.test(pathname)) {
        return 'PR';
      }
      if (/^\/[^/]+\/[^/]+\/issues\//.test(pathname)) {
        return 'issue';
      }
    } catch {
      // fall through to generic label
    }
    return 'GitHub source';
  }
  switch (sourceType) {
    case 'slack':
      return 'Slack thread';
    case 'confluence':
      return 'Confluence page';
    case 'jira':
      return 'Jira issue';
    case 'atlas':
      return 'Atlas resource';
    default:
      return 'source';
  }
}

export interface RolloutEnvironmentSelection {
  environmentKey: string;
  /** One of "simple" | "policy". "simple" turns the flag on immediately at
   *  config-creation time. "policy" resolves the environment's release policy
   *  at scheduler time (after PR merge + flag evaluation begins) and performs
   *  the appropriate release (immediate, progressive, or guarded). */
  releaseType: string;
}

export interface PromptSubmission {
  /** The raw text the user typed. */
  rawPrompt: string;
  /** The enhanced prompt with context baked in. */
  prompt: string;
  repositories: string[];
  /** Selected environments for an AutomatedRollout request, else undefined. */
  environments?: RolloutEnvironmentSelection[];
}

export interface VegaPromptComposerProps {
  /** Called when the user submits a prompt. The consumer decides what to do with it. */
  onSubmit: (submission: PromptSubmission) => void;
  aiChatLimitData?: {
    aiChatLimitStatus: {
      current_count: number;
      limit: number;
    } | null;
  };
  /** Whether the send button is disabled (e.g. while polling). */
  disabled?: boolean;
  /** Whether the component is in an "analyzing" state (shows stop button). */
  isAnalyzing?: boolean;
  /** Called when the user clicks the stop button during analysis. */
  onStopGeneration?: () => void;
  /** Show the "conversation was stopped early" warning. */
  showIncompleteWarning?: boolean;
  /** Whether to show suggested prompts (when there's no conversation history). */
  showSuggestedPrompts?: boolean;
  /** Whether to clear the textarea after submitting. Defaults to true.
   *  Set to false when the consumer navigates away (the unmount handles cleanup). */
  clearOnSubmit?: boolean;
  /** Conversation ID for analytics tracking (provided by AgentInsights for follow-up prompts). */
  conversationId?: string;
  /** Ref that receives a focus callback so external code can focus the textarea. */
  onFocusRef?: RefObject<(() => void) | null>;
  /** Ref that receives a clear callback so external code can clear the draft (e.g. New chat). */
  onClearPromptRef?: RefObject<(() => void) | null>;
  /** When true, render only the send/stop button in the footer — no settings gear, mode picker, or repo
   *  selector. Used by account-scoped skill panels (e.g. pricing concierge) that don't operate on code. */
  simplifiedFooter?: boolean;
  /** The agent response that has been selected by the user (eg: checkbox selections). */
  agentResponse?: string;
}

export const VegaPromptComposer: FC<VegaPromptComposerProps> = ({
  onSubmit,
  aiChatLimitData,
  disabled = false,
  isAnalyzing = false,
  onStopGeneration,
  showIncompleteWarning = false,
  showSuggestedPrompts: showSuggestedPromptsProp = true,
  clearOnSubmit = true,
  conversationId,
  onFocusRef,
  onClearPromptRef,
  agentResponse = '',
  simplifiedFooter = false,
}) => {
  const enableSuggestedPrompts = enableObservabilityAiSuggestedPrompts();
  const automatedRolloutEnabled = enableVegaAutomatedRollout();
  const slug = githubVegaAppUrlSlug();

  const {
    initialQuery,
    context: agentContext,
    clearContext,
    resource,
    preferredRepositories,
    retrievedConversationData,
    conversationClearSignal,
    permissionMode,
    setPermissionMode,
  } = useAgentInsights();

  const vegaMentionsRef = useRef<VegaMentionsInputHandle | null>(null);

  const { projectId, projectKey } = useProjectId();

  // Hide the default initial query when suggested prompts are shown,
  // but never hide an explicit initialQuery (e.g. from flag cleanup).
  const hideInitialQuery = enableSuggestedPrompts && showSuggestedPromptsProp && !initialQuery;
  const initialDraft = useMemo(() => loadDraftFromStorage(), []);
  const [prompt, setPrompt] = useState(initialQuery || initialDraft);
  // ExternalSource items are migrated into the editor as inline mention chips and
  // the context is cleared (see effect below). Capture the source url + sourceType
  // here so the placeholder can stay contextual even after the context is empty.
  const [externalSourceHint, setExternalSourceHint] = useState<{
    url: string;
    sourceType: ExternalSourceType;
  } | null>(null);

  const [selectedRepos, setSelectedRepos] = useLocalStorage<string[] | 'all'>('vega-selected-repositories', []);

  // Vega "automated rollout" mode (gated by enableVegaAutomatedRollout). When
  // active, the user picks which environments Vega should automatically release
  // the new flag in; the selection persists across sessions.
  const [isAutomatedRollout, setIsAutomatedRollout] = useState(false);
  const [selectedEnvKeys, setSelectedEnvKeys] = useLocalStorage<string[]>('vega-selected-environments', []);
  // Per-environment release type ("simple" | "policy"), keyed by env key.
  // Persisted alongside the selection so it survives sessions. Missing entries
  // default to DEFAULT_ROLLOUT_RELEASE_TYPE ("simple").
  const [envReleaseTypes, setEnvReleaseTypes] = useLocalStorage<Record<string, RolloutReleaseType>>(
    'vega-environment-release-types',
    {},
  );

  const handleReleaseTypeChange = useCallback(
    (environmentKey: string, releaseType: RolloutReleaseType) => {
      setEnvReleaseTypes((prev) => ({ ...prev, [environmentKey]: releaseType }));
    },
    [setEnvReleaseTypes],
  );

  const { data: rolloutEnvironments = [] } = useQuery({
    ...getEnvironmentsByProjectQuery({ projectKey, query: { sort: 'critical' } }),
    select: (data) => data.items?.map((env) => ({ key: env.key, name: env.name })) ?? [],
    enabled: automatedRolloutEnabled && isAutomatedRollout,
  });

  const { data: releasePoliciesData } = useQuery({
    ...getReleasePoliciesQuery({ projectKey, header: { 'LD-API-Version': 'beta' } }),
    enabled: automatedRolloutEnabled && isAutomatedRollout && !!projectKey,
  });

  // Compute the applicable release policy for each environment (best-effort,
  // without flag tags since the new flag has none yet).
  const releasePolicyByEnvKey = useMemo<Record<string, ApplicablePolicyInfo | undefined>>(() => {
    const policies = releasePoliciesData?.items ?? [];
    const result: Record<string, ApplicablePolicyInfo | undefined> = {};
    for (const env of rolloutEnvironments) {
      const applicable = getApplicableReleasePolicyForEnvironment(policies, env.key);
      if (!applicable) {
        result[env.key] = undefined;
      } else {
        const method = applicable.releaseMethod;
        const guardedCfg = applicable.guardedReleaseConfig;
        const guardedHasStages = (guardedCfg?.stages?.length ?? 0) > 0;
        const guardedMetricCount = (guardedCfg?.metricKeys?.length ?? 0) + (guardedCfg?.metricGroupKeys?.length ?? 0);
        const guardedHasMetrics = guardedMetricCount > 0;

        const hasStages =
          method === 'immediate-release' ||
          (method === 'progressive-release' && (applicable.progressiveReleaseConfig?.stages?.length ?? 0) > 0) ||
          (method === 'guarded-release' && guardedHasStages && guardedHasMetrics);

        let disabledReason: string | undefined;
        if (!hasStages) {
          if (method === 'guarded-release' && guardedHasStages && !guardedHasMetrics) {
            disabledReason = 'no metrics configured';
          } else {
            disabledReason = 'no steps configured';
          }
        }

        result[env.key] = { key: applicable.key, name: applicable.name, hasStages, disabledReason };
      }
    }
    return result;
  }, [releasePoliciesData, rolloutEnvironments]);

  // When environments are selected or policy data loads, auto-default each
  // env's release type: "policy" if it has an applicable policy, "simple"
  // otherwise. New environments get the right default immediately; existing
  // environments are corrected if their persisted value is now invalid (e.g.
  // "policy" was stored but no policy exists anymore).
  useEffect(() => {
    if (!automatedRolloutEnabled || !isAutomatedRollout || !releasePoliciesData) {
      return;
    }
    setEnvReleaseTypes((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const envKey of selectedEnvKeys) {
        const applicablePolicy = releasePolicyByEnvKey[envKey];
        const ideal: RolloutReleaseType = applicablePolicy?.hasStages ? 'policy' : 'simple';
        if (!(envKey in next)) {
          // Newly selected environment: initialize with the right default.
          next[envKey] = ideal;
          changed = true;
        } else if (next[envKey] === 'policy' && (!applicablePolicy || !applicablePolicy.hasStages)) {
          // Guard stale persisted "policy" selection when no policy exists or
          // the policy has no stages configured.
          next[envKey] = 'simple';
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [
    selectedEnvKeys,
    releasePolicyByEnvKey,
    releasePoliciesData,
    automatedRolloutEnabled,
    isAutomatedRollout,
    setEnvReleaseTypes,
  ]);

  const { data: vegaIntegrationStatus, loading: isLoadingVegaIntegrationStatus } = useGetVegaIntegrationStatusQuery({
    variables: { project_id: projectId },
  });

  // Auto-select repositories when preferredRepositories is provided
  useEffect(() => {
    if (preferredRepositories.length === 0 || isLoadingVegaIntegrationStatus) {
      return;
    }
    const availableRepos = vegaIntegrationStatus?.vega_integration_status?.repositories ?? [];
    if (availableRepos.length === 0) {
      return;
    }
    const preferredLower = preferredRepositories.map((r) => r.toLowerCase());
    const matchedKeys = availableRepos
      .filter((repo) => {
        const repoNameLower = repo.name.toLowerCase();
        return preferredLower.some((pref) => repoNameLower === pref || repoNameLower.endsWith(`/${pref}`));
      })
      .map((repo) => repo.key);
    if (matchedKeys.length > 0) {
      setSelectedRepos(matchedKeys);
    }
  }, [preferredRepositories, isLoadingVegaIntegrationStatus, vegaIntegrationStatus, setSelectedRepos]);

  // Imperatively sync initialQuery into the Tiptap editor because initialContent is only read on mount.
  useEffect(() => {
    if (initialQuery && !hideInitialQuery) {
      setPrompt(initialQuery);
      vegaMentionsRef.current?.setContent(draftToTiptapHtml(initialQuery));
    }
  }, [initialQuery, hideInitialQuery]);

  // Resources attached via contextual entry points ("Ask Vega" on a log/span/etc.) are
  // migrated into the editor as inline mention chips. Historical messages are rendered
  // by UserMessage, which keeps its own ContextPills path for pre-V2 stored conversations.
  useEffect(() => {
    const items = agentContext?.resourceOrQuery ?? [];
    if (items.length === 0 || !vegaMentionsRef.current) {
      return;
    }
    const externalSource = items.find((item) => item.type === 'ExternalSource');
    if (
      externalSource &&
      externalSource.payload &&
      typeof externalSource.payload.url === 'string' &&
      isExternalSourceType(externalSource.payload.sourceType)
    ) {
      // sourceType is narrowed to ExternalSourceType here — no cast needed.
      setExternalSourceHint({
        url: externalSource.payload.url,
        sourceType: externalSource.payload.sourceType,
      });
    }
    items.forEach((r) => vegaMentionsRef.current?.insertMention(r));
    clearContext();
  }, [agentContext?.resourceOrQuery, clearContext]);

  const handleFocus = useCallback(() => {
    vegaMentionsRef.current?.focus();
  }, []);

  useEffect(() => {
    if (onFocusRef) {
      // eslint-disable-next-line no-param-reassign
      onFocusRef.current = handleFocus;
    }
  }, [onFocusRef, handleFocus]);

  // Persist the in-progress draft to localStorage so it survives navigation/refresh
  useEffect(() => {
    saveDraftToStorage(prompt);
  }, [prompt]);

  // Clear the draft and prompt when a historical conversation is loaded. Drop the
  // contextual hint too — it belonged to whatever conversation we were composing
  // before the load, and the new conversation hasn't introduced its own source yet.
  useEffect(() => {
    if (retrievedConversationData) {
      setPrompt('');
      vegaMentionsRef.current?.clear();
      clearDraftFromStorage();
      setExternalSourceHint(null);
    }
  }, [retrievedConversationData]);

  // Clear the draft and prompt when startConversationWithSource triggers a new
  // conversation. The hint is deliberately NOT reset here: startConversationWithSource
  // always attaches a new ExternalSource to agentContext, which the resource
  // migration effect picks up to set the new hint. Resetting here would race the
  // migration write (React 18 batches both state updates in this render cycle and
  // the last write wins — see the bug history at O11Y-1492 review).
  const lastSeenClearSignal = useRef(conversationClearSignal);
  useEffect(() => {
    if (conversationClearSignal !== lastSeenClearSignal.current) {
      lastSeenClearSignal.current = conversationClearSignal;
      setPrompt('');
      vegaMentionsRef.current?.clear();
      clearDraftFromStorage();
    }
  }, [conversationClearSignal]);

  // Expose a clear callback so AgentInsights can clear the draft on "New chat".
  // Resetting the hint here is the path that handles "user opened a PR-seeded
  // conversation, then clicked New chat to start a generic one" — otherwise the
  // placeholder would keep saying "Ask Vega about this PR…" with no source attached.
  const clearPrompt = useCallback(() => {
    setPrompt('');
    vegaMentionsRef.current?.clear();
    clearDraftFromStorage();
    setExternalSourceHint(null);
  }, []);

  useEffect(() => {
    if (onClearPromptRef) {
      // eslint-disable-next-line no-param-reassign
      onClearPromptRef.current = clearPrompt;
    }
  }, [onClearPromptRef, clearPrompt]);

  const { visiblePrompts, exitingKey } = useRotatingPrompts(
    resource,
    enableSuggestedPrompts && showSuggestedPromptsProp,
  );

  const githubNotAuthorized =
    !vegaIntegrationStatus?.vega_integration_status?.userTokenValid &&
    vegaIntegrationStatus?.vega_integration_status?.authLink;
  const githubIntegrated = vegaIntegrationStatus?.vega_integration_status?.userTokenValid;

  // PR-writing flows require the GitHub app, but the model handles that via
  // a connect nudge in the prompt copy rather than blocking submit.
  const handleConnectGitHub = useCallback(() => {
    trackVegaAiGithubConnectClicked();
    const baseUrl = githubNotAuthorized
      ? vegaIntegrationStatus?.vega_integration_status?.authLink
      : `https://github.com/apps/${slug}/installations/new`;
    if (baseUrl) {
      const url = new URL(baseUrl);
      url.searchParams.set('state', window.location.href);
      window.location.href = url.toString();
    }
  }, [githubNotAuthorized, vegaIntegrationStatus, slug]);

  const navigate = useNavigate();
  const handleOpenSettings = useCallback(() => {
    navigate(toHref(toVegaSettings({ projectKey })));
  }, [navigate, projectKey]);
  const allRepos = vegaIntegrationStatus?.vega_integration_status?.repositories || [];
  const privateRepos = allRepos.filter((repo) => !repo.is_public);
  const privateRepoKeys = privateRepos.map((repo) => repo.key);
  const effectiveSelectedRepos =
    selectedRepos === 'all' ? privateRepoKeys : selectedRepos.filter((key) => privateRepoKeys.includes(key));
  const selectedReposNames = privateRepos
    .filter((repo) => effectiveSelectedRepos.includes(repo.key))
    .map((repo) => repo.name);

  const handleSendPrompt = useCallback(
    (promptText?: string, isSuggestedPrompt = false) => {
      let actualPrompt = promptText || prompt;
      if (agentResponse) {
        actualPrompt = `User selection: ${agentResponse}\n\n${actualPrompt}`;
      }
      if (!actualPrompt.trim()) {
        return;
      }

      // Collect mention resources before clearing the editor
      const mentionResources = vegaMentionsRef.current?.getMentionResources() ?? [];

      if (clearOnSubmit) {
        setPrompt('');
        vegaMentionsRef.current?.clear();
      }

      // Build the prompt with context data.
      // Automated rollout carries the selected environments so the backend can
      // record a rollout config after the PR (signalled by the environments
      // field, not a request type).
      const automatedRolloutActive = automatedRolloutEnabled && isAutomatedRollout;
      let environments: RolloutEnvironmentSelection[] | undefined;
      if (automatedRolloutActive) {
        environments = selectedEnvKeys.map((environmentKey) => {
          const persisted = envReleaseTypes[environmentKey] ?? DEFAULT_ROLLOUT_RELEASE_TYPE;
          // Guard stale "policy" selections: if the env has no applicable policy
          // (or the policy has no stages) at submit time, fall back to "simple"
          // so the backend doesn't reject it.
          const releaseType: string =
            persisted === 'policy' &&
            (!releasePolicyByEnvKey[environmentKey] || !releasePolicyByEnvKey[environmentKey]?.hasStages)
              ? 'simple'
              : persisted;
          return { environmentKey, releaseType };
        });
      }
      const allResources = mergeAndDedupeResources(agentContext?.resourceOrQuery ?? [], mentionResources);
      let enhancedPrompt = buildPrompt(actualPrompt, allResources);
      // Surface the per-environment release plan to the model so it records the
      // rollout config with the exact release type the user chose for each
      // environment. The structured `environments` field is also sent, but the
      // model only reads the prompt text.
      if (automatedRolloutActive && environments && environments.length > 0) {
        const planLines = environments.map((env) => `- ${env.environmentKey}: ${env.releaseType}`).join('\n');
        enhancedPrompt = `${enhancedPrompt}\n\nAutomated rollout plan — when recording the rollout config, use these exact per-environment release types:\n${planLines}`;
      }

      trackVegaAiPromptSent(permissionMode, {
        hasContext: allResources.length > 0,
        contextCount: allResources.length,
        isSuggestedPrompt,
        conversationId: conversationId || undefined,
      });

      onSubmit({
        rawPrompt: actualPrompt,
        prompt: enhancedPrompt,
        repositories: selectedReposNames,
        environments,
      });
    },
    [
      prompt,
      agentResponse,
      agentContext,
      permissionMode,
      selectedReposNames,
      onSubmit,
      clearOnSubmit,
      conversationId,
      automatedRolloutEnabled,
      isAutomatedRollout,
      selectedEnvKeys,
      envReleaseTypes,
      releasePolicyByEnvKey,
    ],
  );

  const isAtUsageLimit =
    !!aiChatLimitData?.aiChatLimitStatus &&
    aiChatLimitData.aiChatLimitStatus.limit > 0 &&
    aiChatLimitData.aiChatLimitStatus.current_count >= aiChatLimitData.aiChatLimitStatus.limit;
  const isPromptEmpty = !isAnalyzing && !prompt.trim() && !agentResponse.trim();

  const getModeIcon = (): IconName => {
    if (permissionMode === 'read-write') {
      return 'graduation-cap';
    }
    return 'text-box-search';
  };

  const getPlaceholderText = () => {
    const atHint = ' — type @ to attach context';
    if (automatedRolloutEnabled && isAutomatedRollout) {
      return `Describe the change to roll out behind a new flag (Cmd/Ctrl + Enter to send)${atHint}`;
    }
    if (externalSourceHint) {
      const label = externalSourceLabel(externalSourceHint);
      return `Ask ${AI_BOT_NAME} about this ${label} (Cmd/Ctrl + Enter to send)${atHint}`;
    }
    return `Ask ${AI_BOT_NAME} a question (Cmd/Ctrl + Enter to send)${atHint}`;
  };

  return (
    <>
      {enableSuggestedPrompts &&
        showSuggestedPromptsProp &&
        visiblePrompts.length > 0 &&
        !isAnalyzing &&
        !disabled &&
        !isAtUsageLimit && (
          <Box padding="8" paddingLeft="16" paddingRight="16" display="flex" flexDirection="column" gap="12">
            <Text size="medium" weight="bold">
              Suggested prompts
            </Text>
            <Box display="flex" flexDirection="row" gap="8" flexWrap="wrap" width="full">
              {visiblePrompts.map((sp) => (
                <div key={sp.key} className={exitingKey === sp.key ? styles.promptExit : styles.promptEnter}>
                  <Button
                    onPress={() => {
                      trackVegaAiSuggestedPromptClicked(sp.text);
                      handleSendPrompt(sp.text, true);
                    }}
                    variant="default"
                  >
                    <Icon name="brain" size="small" />
                    {sp.text}
                  </Button>
                </div>
              ))}
            </Box>
          </Box>
        )}

      <Box cssClass={styles.textAreaContainer}>
        {/* Incomplete conversation warning */}
        {showIncompleteWarning && (
          <Box marginTop="4" marginBottom="4" marginLeft="8" marginRight="8">
            <Callout kind="warning">
              <Text size="small">Conversation stopped — send a message to continue, or start a new chat</Text>
            </Callout>
          </Box>
        )}

        {/* Chat limit warning indicator */}
        {aiChatLimitData?.aiChatLimitStatus &&
          aiChatLimitData.aiChatLimitStatus.limit > 0 &&
          aiChatLimitData.aiChatLimitStatus.current_count / aiChatLimitData.aiChatLimitStatus.limit > 0.66 && (
            <Box marginTop="4" marginBottom="4" marginLeft="8" marginRight="8">
              <Button
                size="small"
                variant="default"
                style={{ width: '100%', justifyContent: 'space-between' }}
                onPress={() => {
                  window.open(toDiagnosticUsage().pathname, '_blank');
                }}
              >
                <Stack direction="row" gap="6" alignItems="center" cssClass={styles.secondaryText}>
                  <Icon name="warning" size="small" className={styles.secondaryIcon} />
                  <Text size="small">
                    {aiChatLimitData.aiChatLimitStatus.current_count} / {aiChatLimitData.aiChatLimitStatus.limit}{' '}
                    prompts used
                  </Text>
                </Stack>
                <Icon name="link-external" size="small" />
              </Button>
            </Box>
          )}

        {/* Input area */}
        <Box
          padding="8"
          backgroundColor="default"
          borderRadius="8"
          style={{ border: `1px solid ${themeVars.static.divider.default}` }}
        >
          <VegaMentionsInput
            ref={vegaMentionsRef}
            placeholder={getPlaceholderText()}
            disabled={disabled || isAnalyzing}
            initialContent={initialQuery || draftToTiptapHtml(initialDraft)}
            onTextChange={setPrompt}
            onSubmit={() => {
              if (!isAtUsageLimit) {
                handleSendPrompt();
              }
            }}
          />
          {simplifiedFooter ? (
            <Box display="flex" justifyContent="flex-end" alignItems="center">
              <SendStopButton
                isAnalyzing={isAnalyzing}
                disabled={disabled}
                isPromptEmpty={isPromptEmpty}
                isAtUsageLimit={isAtUsageLimit}
                onStopGeneration={onStopGeneration}
                handleSendPrompt={handleSendPrompt}
              />
            </Box>
          ) : (
            <Box display="flex" justifyContent="space-between" alignItems="center">
              {/* AI Mode Selector */}
              <Stack direction="row" gap="4" alignItems="center">
                <IconButton
                  icon="gear-outline"
                  size="small"
                  aria-label="Vega settings"
                  onPress={handleOpenSettings}
                  isDisabled={disabled || isAnalyzing}
                />
                <PermissionModePicker
                  permissionMode={permissionMode}
                  githubIntegrated={Boolean(githubIntegrated)}
                  disabled={disabled || isAnalyzing}
                  iconLeft={getModeIcon()}
                  onChange={(next) => {
                    if (next !== permissionMode) {
                      trackVegaAiModeChanged(next);
                      setPermissionMode(next);
                    }
                  }}
                />
                {automatedRolloutEnabled && githubIntegrated && (
                  <Button
                    size="small"
                    variant={isAutomatedRollout ? 'primary' : 'minimal'}
                    isDisabled={disabled || isAnalyzing}
                    onPress={() => setIsAutomatedRollout((prev) => !prev)}
                    className={isAutomatedRollout ? undefined : styles.secondaryText}
                  >
                    <Icon name="rocket" size="small" /> Automated release
                  </Button>
                )}
              </Stack>

              {/* Right side: (skills-ui) repo selector + small submit; (legacy) submit only */}
              <Stack direction="row" gap="4" alignItems="center">
                {automatedRolloutEnabled && isAutomatedRollout && githubIntegrated && (
                  <RolloutEnvironmentSelector
                    environments={rolloutEnvironments}
                    selectedKeys={selectedEnvKeys}
                    onSelectionChange={setSelectedEnvKeys}
                    releaseTypes={envReleaseTypes}
                    onReleaseTypeChange={handleReleaseTypeChange}
                    releasePolicyByEnvKey={releasePolicyByEnvKey}
                    projectKey={projectKey}
                    disabled={disabled || isAnalyzing}
                    triggerClassName={styles.secondaryText}
                    placement="top right"
                    size="small"
                  />
                )}
                {isLoadingVegaIntegrationStatus && (
                  <Button size="small" variant="minimal" isDisabled className={styles.secondaryText}>
                    <IconSolidGithub className={styles.secondaryIcon} size={14} /> Loading repositories…
                  </Button>
                )}
                {githubIntegrated && (
                  <GitHubRepoSelector
                    repos={allRepos}
                    selectedKeys={effectiveSelectedRepos}
                    onSelectionChange={(keys) => setSelectedRepos(keys)}
                    disabled={disabled || isAnalyzing}
                    triggerClassName={styles.secondaryText}
                    placement="top right"
                    githubIcon={<IconSolidGithub className={styles.secondaryIcon} size={14} />}
                    size="small"
                    headerContent={
                      <Box px="8" pt="8">
                        <Callout kind="warning">
                          <Text size="xSmall">
                            Please note that code in selected repositories may be cloned in an isolated environment as
                            part of investigations.{' '}
                            <a
                              href={getDocumentationUrl('home/observability/vega')}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Learn more
                            </a>
                          </Text>
                        </Callout>
                      </Box>
                    }
                  />
                )}
                {!githubIntegrated && !isLoadingVegaIntegrationStatus && (
                  <Button
                    size="small"
                    variant="default"
                    onPress={handleConnectGitHub}
                    isDisabled={disabled || isAnalyzing}
                  >
                    <IconSolidGithub size={14} />
                    Connect GitHub
                  </Button>
                )}

                <SendStopButton
                  isAnalyzing={isAnalyzing}
                  disabled={disabled}
                  isPromptEmpty={isPromptEmpty}
                  isAtUsageLimit={isAtUsageLimit}
                  onStopGeneration={onStopGeneration}
                  handleSendPrompt={handleSendPrompt}
                />
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};
